/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import { isArrayNotEmpty } from "utils/empty-exists";
import { Button, Stack } from "@mui/material";
import { SVGIcons } from "cms/icons/svg-icons";
import { useSortableComponentList } from "cms/components/hooks/use-sortable-component-list";
import { IconLinkContext } from "cms/components/components/icon-links-list/edit/icon-link-context";
import { AddLineWithIcon } from "components/add-line-with-icon";
import PositionButtons from "components/position-buttons";
import RemoveIcon from "components/remove-icon";
import { SortableContent } from "components/sortable-content";

const defaultIconLink = Object.freeze({
    icon: "",
    link: {},
});

/**
 * @param {object} props
 * @param {FeaturedWebpage[]} props.icon_links
 * @param {(icon_links: IconLink[]) => void} props.onChangeList
 */
const IconLinkEditWrapper = (props) => {

    const { t } = useTranslation();
    const {
        icon_links: initialIconLinks = [],
        onChangeList,
        children,
    } = props;

    if (typeof onChangeList !== "function") {
        throw Error("onChangeList was not supplied, or is not a function");
    }

    const {
        list,
        sortableMethods,
        controls,
    } = useSortableComponentList( initialIconLinks, defaultIconLink, onChangeList);

    const {
        addNewItem,
        addNewItemBelow,
        removeItem,
        updateItems
    } = controls;

    return (
        <Fragment>
            <div style={{ display: "flex", flexDirection: "row",  }}>
                <div style={{ fontWeight: "700", padding: "4px 12px" }}>
                    {t("commons.links")}
                </div>
                <div>
                    <Button
                        variant="text"
                        color={"primary"}
                        onClick={addNewItem}
                    >
                        <SVGIcons.common.Create style={{ paddingRight: 4, cursor: "pointer" }}/>
                        {t("commons.add")}
                    </Button>
                </div>
            </div>
            <div className='simplicity-icon-links-wrapper'>
                <Stack direction='column'>
                    {isArrayNotEmpty(list) && list.map((item, index) => {
                        const { guid } = item;
                        const context = {
                            list,
                            guid,
                            item,
                            index,
                            position: index,
                            addNewItem,
                            updateItems,
                            removeItem,
                        };
                        return (
                            <IconLinkContext.Provider
                                key={`icon-link-${guid}`}
                                value={context}
                            >
                                <SortableContent
                                    id={guid}
                                    index={index}
                                    sortableMethods={sortableMethods}
                                >

                                    {children}

                                    <div>
                                        <PositionButtons
                                            index={index}
                                            list={list}
                                            handleUpdate={updateItems}
                                        />
                                        <RemoveIcon
                                            className='simplicity-composer-field-editor-remove'
                                            onDeleteItem={() => removeItem(index)}
                                            index={index}
                                        />
                                    </div>
                                </SortableContent>
                                <AddLineWithIcon
                                    addNewItem={() => addNewItemBelow(index)}
                                />
                            </IconLinkContext.Provider>
                        );
                    })}
                </Stack>
            </div>
        </Fragment>
    );
};
IconLinkEditWrapper.displayName = "IconLinkEditWrapper";
export { IconLinkEditWrapper };
